import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTelegram } from '@/hooks/useTelegram';

interface Monster {
  name: string;
  tags: string[];
  hp: number;
  armor: number;
  damage: string;
  instinct: string;
  moves: string[];
  description: string;
}

const monsters: Monster[] = [
  {
    name: 'Гоблин',
    tags: ['Орда', 'Маленький', 'Разумный', 'Организованный'],
    hp: 3,
    armor: 1,
    damage: 'd6',
    instinct: 'Набигать и грабить',
    moves: [
      'Напасть из засады',
      'Позвать подкрепление',
      'Украсть что-то ценное',
    ],
    description: 'Мелкие, злобные и многочисленные. Гоблины компенсируют слабость числом и хитростью.',
  },
  {
    name: 'Орк',
    tags: ['Группа', 'Разумный', 'Организованный'],
    hp: 6,
    armor: 1,
    damage: 'd8+2',
    instinct: 'Завоёвывать и разрушать',
    moves: [
      'Атаковать с яростью',
      'Запугать криком',
      'Сломать что-то важное',
    ],
    description: 'Жестокие воины, живущие войной. Сильнее людей и без жалости.',
  },
  {
    name: 'Скелет',
    tags: ['Группа'],
    hp: 7,
    armor: 1,
    damage: 'd6',
    instinct: 'Исполнять приказы хозяина',
    moves: [
      'Атаковать без страха и боли',
      'Подняться снова',
      'Действовать по команде некроманта',
    ],
    description: 'Кости мёртвых, поднятые тёмной магией. Не чувствуют боли и не знают страха.',
  },
  {
    name: 'Зомби',
    tags: ['Орда'],
    hp: 11,
    armor: 1,
    damage: 'd6',
    instinct: 'Пожирать живых',
    moves: [
      'Схватить и не отпускать',
      'Передать заразу укусом',
      'Подняться из мёртвых',
    ],
    description: 'Гниющие трупы, движимые голодом. Медленные, но неостановимые.',
  },
  {
    name: 'Огр',
    tags: ['Одиночка', 'Большой'],
    hp: 12,
    armor: 1,
    damage: 'd10+3',
    instinct: 'Есть и крушить',
    moves: [
      'Схватить и швырнуть',
      'Разрушить укрытие',
      'Впасть в ярость',
    ],
    description: 'Огромные, тупые и голодные. Огры решают проблемы грубой силой.',
  },
  {
    name: 'Тролль',
    tags: ['Одиночка', 'Большой'],
    hp: 20,
    armor: 1,
    damage: 'd10+3',
    instinct: 'Охотиться и есть',
    moves: [
      'Регенерировать урон',
      'Разорвать на части',
      'Выследить по запаху',
    ],
    description: 'Регенерирующие чудовища. Только огонь и кислота могут остановить их исцеление.',
  },
  {
    name: 'Молодой дракон',
    tags: ['Одиночка', 'Большой', 'Разумный', 'Накопитель'],
    hp: 16,
    armor: 3,
    damage: 'd10+2',
    instinct: 'Копить сокровища',
    moves: [
      'Дохнуть огнём',
      'Атаковать когтями и зубами',
      'Взлететь с добычей',
    ],
    description: 'Молодой, но уже смертельно опасный. Копит первые сокровища.',
  },
  {
    name: 'Древний дракон',
    tags: ['Одиночка', 'Огромный', 'Разумный', 'Накопитель', 'Ужасающий'],
    hp: 24,
    armor: 5,
    damage: 'd12+5',
    instinct: 'Править как бог',
    moves: [
      'Испепелить всё вокруг',
      'Говорить как с насекомыми',
      'Использовать древнюю магию',
      'Разрушить укрепления одним ударом',
    ],
    description: 'Легенда во плоти. Древние драконы — это силы природы, а не просто монстры.',
  },
];

export function MonstersPage() {
  const navigate = useNavigate();
  const { hapticFeedback } = useTelegram();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-gray-900 safe-area-top safe-area-bottom">
      {/* Header */}
      <div className="sticky top-0 bg-gray-900/95 backdrop-blur-sm border-b border-gray-800 z-10">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={() => {
              hapticFeedback('light');
              navigate('/gm-tools');
            }}
            className="text-gray-400 hover:text-white transition-colors"
          >
            ← Назад
          </button>
          <h1 className="text-lg font-bold text-white">Монстры</h1>
          <div className="w-16" />
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-gray-400 text-sm mb-6"
        >
          Нажмите на монстра, чтобы увидеть его ходы и описание.
        </motion.p>

        {monsters.map((monster, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.03 * index }}
          >
            <button
              onClick={() => {
                hapticFeedback('light');
                setExpandedIndex(expandedIndex === index ? null : index);
              }}
              className="w-full p-4 rounded-xl bg-gray-800/50 border border-gray-700/50 text-left"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-white">{monster.name}</h3>
                <span className="text-gray-500">
                  {expandedIndex === index ? '▲' : '▼'}
                </span>
              </div>

              <div className="flex gap-4 text-sm">
                <span className="text-red-400">❤️ {monster.hp}</span>
                <span className="text-blue-400">🛡️ {monster.armor}</span>
                <span className="text-amber-400">⚔️ {monster.damage}</span>
              </div>

              <div className="flex flex-wrap gap-1 mt-2">
                {monster.tags.map((tag, i) => (
                  <span key={i} className="px-2 py-0.5 text-xs rounded bg-gray-700 text-gray-300">
                    {tag}
                  </span>
                ))}
              </div>
            </button>

            <AnimatePresence>
              {expandedIndex === index && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="p-4 bg-gray-800/30 rounded-b-xl border-x border-b border-gray-700/50 space-y-3">
                    <p className="text-gray-300 text-sm">{monster.description}</p>

                    <div>
                      <p className="text-amber-400 text-sm font-medium mb-1">
                        Инстинкт: {monster.instinct}
                      </p>
                    </div>

                    <div>
                      <p className="text-gray-400 text-sm font-medium mb-1">Ходы:</p>
                      <ul className="space-y-1">
                        {monster.moves.map((move, i) => (
                          <li key={i} className="text-gray-300 text-sm">• {move}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
